import { useQuery } from '@tanstack/react-query'
import { Printer, Wallet } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { EmptyState } from '@/components/feedback/States'
import { DataTable, type Column } from '@/components/tables/DataTable'
import { FilterBar, SearchInput } from '@/components/tables/FilterBar'
import { Pagination } from '@/components/tables/Pagination'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { paymentsQuery } from '@/features/finance/api'
import { localizePeriodLabel, methodLabel } from '@/features/finance/status'
import { PAYMENT_METHODS, type Payment } from '@/features/finance/types'
import { useMoney } from '@/features/finance/useCurrency'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useListParams } from '@/hooks/useListParams'
import { cn } from '@/utils/cn'
import { formatDate } from '@/utils/format'

const FILTERS = ['method', 'from', 'to', 'state'] as const

export function PaymentsPage() {
  const { t } = useTranslation('finance')
  useDocumentTitle('Payments')
  const navigate = useNavigate()
  const { money } = useMoney()
  const { state, query, update, toggleSort, setPage } = useListParams(FILTERS, { sort: 'paidOn' })
  const [params] = useSearchParams()
  const list = useQuery(
    paymentsQuery({ ...query, direction: params.has('direction') ? query.direction : 'desc' }),
  )

  const columns: Column<Payment>[] = [
    {
      key: 'receipt',
      header: t('Receipt'),
      sortKey: 'receiptNumber',
      cell: (p) => (
        <div className="min-w-0">
          <span
            className={cn('block font-mono text-xs font-medium text-ink', p.isReversed && 'line-through')}
          >
            {p.receiptNumber}
          </span>
          <Link
            to={`/admin/finance/invoices/${p.invoice.id}`}
            onClick={(e) => e.stopPropagation()}
            className="block text-xs text-muted hover:text-brand-700"
          >
            {p.invoice.number} · {localizePeriodLabel(p.invoice.periodLabel)}
          </Link>
        </div>
      ),
    },
    {
      key: 'student',
      header: t('Student'),
      sortKey: 'student',
      cell: (p) => (
        <div className="min-w-0">
          <span className="block truncate font-medium text-ink">{p.student.name}</span>
          <span className="block text-xs text-muted">{p.student.studentCode}</span>
        </div>
      ),
    },
    { key: 'paidOn', header: t('Paid on'), sortKey: 'paidOn', cell: (p) => formatDate(p.paidOn) },
    {
      key: 'method',
      header: t('Method'),
      hideBelow: 'md',
      cell: (p) => (
        <div className="min-w-0">
          <span className="block">{t(methodLabel[p.method])}</span>
          {p.reference ? <span className="block truncate text-xs text-muted">{p.reference}</span> : null}
        </div>
      ),
    },
    { key: 'by', header: t('Received by'), hideBelow: 'xl', cell: (p) => p.receivedBy ?? '—' },
    {
      key: 'amount',
      header: t('Amount'),
      sortKey: 'amount',
      align: 'right',
      cell: (p) => (
        <span className={cn('font-medium tabular', p.isReversed ? 'text-muted line-through' : 'text-ink')}>
          {money(p.amount)}
        </span>
      ),
    },
    {
      key: 'state',
      header: t('Status'),
      hideBelow: 'sm',
      cell: (p) =>
        p.isReversed ? (
          <Badge tone="danger" size="sm" dot>
            {t('Reversed')}
          </Badge>
        ) : (
          <Badge tone="success" size="sm" dot>
            {t('Valid')}
          </Badge>
        ),
    },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Actions')}</span>,
      align: 'right',
      cell: (p) => (
        <Button
          asChild
          variant="ghost"
          size="icon-sm"
          aria-label={t('Print receipt {{number}}', { number: p.receiptNumber })}
          title={t('Print receipt')}
        >
          <Link to={`/admin/finance/payments/${p.id}/receipt`} onClick={(e) => e.stopPropagation()}>
            <Printer />
          </Link>
        </Button>
      ),
    },
  ]

  const hasFilters = Boolean(state.search || Object.keys(state.filters).length)

  return (
    <>
      <PageHeader
        title={t('Payments')}
        description={t('Every payment received, with printable receipts. Reversed payments stay on record.')}
        breadcrumbs={[{ label: t('Finance') }, { label: t('Payments') }]}
        homeHref="/admin"
      />
      <Card>
        <FilterBar
          actions={
            hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  update({
                    search: undefined,
                    method: undefined,
                    from: undefined,
                    to: undefined,
                    state: undefined,
                  })
                }
              >
                {t('Clear filters')}
              </Button>
            ) : null
          }
        >
          <SearchInput
            value={state.search}
            onChange={(v) => update({ search: v })}
            placeholder={t('Receipt, invoice, reference or student')}
            label={t('Search payments')}
          />
          <Select
            aria-label={t('Filter by method')}
            className="sm:w-auto sm:min-w-40"
            placeholder={t('Any method')}
            value={state.filters.method ?? ''}
            onChange={(e) => update({ method: e.target.value })}
            options={PAYMENT_METHODS.map((m) => ({ value: m, label: t(methodLabel[m]) }))}
          />
          <Select
            aria-label={t('Filter by status')}
            className="sm:w-auto sm:min-w-36"
            placeholder={t('Any status')}
            value={state.filters.state ?? ''}
            onChange={(e) => update({ state: e.target.value })}
            options={[
              { value: 'valid', label: t('Valid') },
              { value: 'reversed', label: t('Reversed') },
            ]}
          />
          <label htmlFor="payments-from" className="flex items-center gap-2 text-xs text-muted">
            {t('From')}
            <Input
              id="payments-from"
              type="date"
              size="sm"
              className="sm:w-auto sm:min-w-40"
              value={state.filters.from ?? ''}
              onChange={(e) => update({ from: e.target.value })}
            />
          </label>
          <label htmlFor="payments-to" className="flex items-center gap-2 text-xs text-muted">
            {t('To')}
            <Input
              id="payments-to"
              type="date"
              size="sm"
              className="sm:w-auto sm:min-w-40"
              value={state.filters.to ?? ''}
              onChange={(e) => update({ to: e.target.value })}
            />
          </label>
        </FilterBar>
        <DataTable
          caption={t('Payments')}
          columns={columns}
          rows={list.data?.data}
          getRowId={(p) => p.id}
          loading={list.isPending}
          error={list.error}
          onRetry={() => void list.refetch()}
          sort={{ key: state.sort, direction: state.direction }}
          onSortChange={toggleSort}
          onRowClick={(p) => void navigate(`/admin/finance/invoices/${p.invoice.id}`)}
          mobileCard={(p) => (
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-ink">{p.student.name}</p>
                <p className="truncate text-xs text-muted">
                  {p.receiptNumber} · {t(methodLabel[p.method])} · {formatDate(p.paidOn)}
                </p>
              </div>
              <div className="text-right">
                <p
                  className={cn(
                    'text-sm font-semibold tabular',
                    p.isReversed ? 'text-muted line-through' : 'text-ink',
                  )}
                >
                  {money(p.amount)}
                </p>
                {p.isReversed ? (
                  <Badge tone="danger" size="sm">
                    {t('Reversed')}
                  </Badge>
                ) : null}
              </div>
            </div>
          )}
          empty={
            <EmptyState
              icon={<Wallet />}
              title={hasFilters ? t('No payments match these filters') : t('No payments yet')}
              description={
                hasFilters
                  ? t('Try another method or date range.')
                  : t('Payments recorded on invoices appear here.')
              }
              compact
            />
          }
        />
        <Pagination
          meta={list.data?.meta}
          onPageChange={setPage}
          onPerPageChange={(n) => update({ perPage: n })}
        />
      </Card>
    </>
  )
}
